import {defineSlotRecipe} from '@chakra-ui/react3'

export const priceButtonGroupVariantMap = {
    product: 'default',
    lifestyle: 'on-media'
}

/**
 * recipe for Highlight:
 *
 * slots:
 * - root
 * - darkenLayer
 * - contentContainer
 * - textContainer
 * - subline
 * - headline
 * - copy
 * - buttons
 * - assetContainer
 *
 * variants:
 * - variant: product | lifestyle
 * - desktopImageAlignment: right | left
 * - darken: 0% | 40% | 80%
 *
 * compoundVariants (lifestyle):
 * - darken 0% → darkenLayer transparent
 * - right + 40% / 80% → gradient.left.darken-40 / 80 (combo on base)
 * - left + 40% / 80% → gradient.right.darken-40 / 80 (combo on base)
 *
 * defaultVariants:
 * - variant: product
 * - desktopImageAlignment: right
 * - darken: 0%
 */

export default defineSlotRecipe({
    slots: [
        'root',
        'darkenLayer',
        'widthContainer',
        'contentContainer',
        'textContainer',
        'headline',
        'subline',
        'copy',
        'buttons',
        'assetContainer',
        'overlay'
    ],
    base: {
        root: {
            display: 'flex',
            flexDirection: {base: 'column', s: 'row'},
            backgroundColor: 'var(--module-surface-bg-color)',
            overflow: 'hidden',
            position: 'relative'
        },
        darkenLayer: {
            position: 'absolute',
            pointerEvents: 'none',
            zIndex: 1,
            top: 0,
            bottom: 0,
            left: 'calc(var(--extra) * -1)',
            right: 'calc(var(--extra) * -1)'
        },
        widthContainer: {
            display: 'flex',
            flexDirection: {base: 'column', s: 'row'},
            position: 'relative',
            zIndex: 2,
            '--desktopTextContainerWidth': '360px',
            '--extra': 'max(0px, calc((100cqw - var(--leap-sizes-container-page-max-width)) / 2))'
        },
        contentContainer: {
            py: {base: 'spacing-40', s: 'spacing-64'},
            pl: {base: 'spacing-24'},
            pr: {base: 'spacing-24'},
            display: 'flex',
            flexDirection: 'column',
            width: 'fit-content',
            zIndex: 3,
            order: 1
        },
        textContainer: {
            width: {base: '100%', xs: '75%', s: 'var(--desktopTextContainerWidth)'}
        },
        subline: {color: 'text.brand-default', textStyle: 'body.small'},
        headline: {
            pt: {base: 'spacing-40', s: 'spacing-32'},
            color: 'text.primary',
            textStyle: 'display.small'
        },
        copy: {
            pt: {base: 'spacing-40', s: 'spacing-32'},
            pr: {base: 'spacing-32', s: 'spacing-64'},
            color: 'text.secondary',
            textStyle: 'body.medium'
        },
        buttons: {mt: {base: 'spacing-40', s: 'auto'}},
        assetContainer: {
            width: '100%',
            zIndex: 2,
            order: 2,
            overflow: 'hidden'
        },
        overlay: {position: 'absolute', zIndex: 'overlay', top: 'spacing-24', right: 'spacing-24'}
    },
    variants: {
        variant: {
            product: {
                darkenLayer: {
                    bg: 'gradient.combo.beige-darken-bottom'
                },
                assetContainer: {
                    aspectRatio: {base: '1/1', s: '5/4'},
                    '--contentSideWidth':
                        'calc(var(--desktopTextContainerWidth) + var(--leap-spacing-spacing-90)  + var(--leap-spacing-spacing-40))',
                    width: 'calc(100% + var(--extra))',
                    maxHeight: {
                        s: 'calc((var(--leap-sizes-container-page-max-width) - var(--contentSideWidth)) * 4 / 5)'
                    }
                }
            },
            lifestyle: {
                contentContainer: {
                    position: 'absolute',
                    height: {s: '100%'}
                },
                subline: {display: 'none'},
                headline: {
                    pt: 0,
                    color: 'text.on-media.primary'
                },
                copy: {
                    color: 'text.on-media.primary'
                },
                assetContainer: {
                    aspectRatio: {base: '9/16', s: '16/9'},
                    zIndex: 1,
                    width: 'calc(100% + var(--extra) * 2)',
                    marginInline: 'calc(var(--extra) * -1)',
                    maxHeight: {s: 'calc(var(--leap-sizes-container-page-max-width) * 9 / 16)'}
                },
                darkenLayer: {zIndex: 2}
            }
        },
        desktopImageAlignment: {
            right: {
                contentContainer: {
                    order: {s: 1},
                    pl: {s: 'spacing-90'}, // affects the asset's height
                    pr: {s: 'spacing-40'} // affects the asset's height
                },
                assetContainer: {
                    order: {s: 2},
                    marginRight: 'calc(var(--extra) * -1)'
                }
            },
            left: {
                contentContainer: {
                    order: {s: 2},
                    pl: {s: 'spacing-40'}, // affects the asset's height
                    pr: {s: 'spacing-90'} // affects the asset's height
                },
                assetContainer: {
                    order: {s: 1},
                    marginLeft: 'calc(var(--extra) * -1)'
                }
            }
        },
        darken: {}
    },
    compoundVariants: [
        {
            variant: 'lifestyle',
            desktopImageAlignment: 'right',
            darken: '0%',
            css: {
                darkenLayer: {
                    bg: 'transparent'
                },
                contentContainer: {
                    left: 0
                }
            }
        },
        {
            variant: 'lifestyle',
            desktopImageAlignment: 'right',
            darken: '40%',
            css: {
                darkenLayer: {
                    bg: {
                        base: 'gradient.combo.darken-40-left-top',
                        s: 'gradient.left.darken-40'
                    }
                },
                contentContainer: {
                    left: 0,
                    height: {s: '100%'}
                }
            }
        },
        {
            variant: 'lifestyle',
            desktopImageAlignment: 'right',
            darken: '80%',
            css: {
                darkenLayer: {
                    bg: {
                        base: 'gradient.combo.darken-80-left-top',
                        s: 'gradient.left.darken-80'
                    }
                },
                contentContainer: {
                    left: 0
                }
            }
        },
        {
            variant: 'lifestyle',
            desktopImageAlignment: 'left',
            darken: '0%',
            css: {
                darkenLayer: {
                    bg: 'transparent'
                },
                contentContainer: {
                    right: 0
                }
            }
        },
        {
            variant: 'lifestyle',
            desktopImageAlignment: 'left',
            darken: '40%',
            css: {
                darkenLayer: {
                    bg: {
                        base: 'gradient.combo.darken-40-left-top',
                        s: 'gradient.right.darken-40'
                    }
                },
                contentContainer: {
                    right: 0
                }
            }
        },
        {
            variant: 'lifestyle',
            desktopImageAlignment: 'left',
            darken: '80%',
            css: {
                darkenLayer: {
                    bg: {
                        base: 'gradient.combo.darken-80-left-top',
                        s: 'gradient.right.darken-80'
                    }
                },
                contentContainer: {
                    right: 0
                }
            }
        }
    ],
    defaultVariants: {
        darken: '0%',
        desktopImageAlignment: 'right',
        variant: 'product'
    }
})
